import React, { useState } from 'react';
import { CheckCircle, X, Trash2, Bell, Upload, Paperclip } from 'lucide-react';
import {
  apiAddMilestone, apiUpdateMilestone, apiNotifyMilestone,
  apiDeleteMilestoneFile, apiNotifyMilestoneFiles,
} from '../../api';
import { uploadMilestoneFile, MAX_UPLOAD_BYTES, MAX_UPLOAD_MB } from '../../uploads';
import FileLink from '../FileLink';
import ModalPortal from '../Modal/ModalPortal';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { useAuth } from '../../context';
import { canDeleteTeamContent } from '../../permissions';

/**
 * Milestone add/edit modal (extracted verbatim from the legacy AdminPortal so the
 * new ProjectPage reuses the proven upload / notify / approvals logic).
 */
export default function MilestoneModal({ project, milestone, jwtToken, zohoConnected, client, onClose, onSuccess, onRefreshClient }) {
  const { user } = useAuth();
  const isEdit = !!milestone;
  const [form, setForm] = useState({
    title: milestone?.title || '',
    description: milestone?.description || '',
    status: milestone?.status || 'in_process',
    message: milestone?.message || '',
  });
  const [entries, setEntries] = useState([]); // { file, status:'pending'|'uploading'|'saving'|'done'|'error', progress, error }
  const [attachedFiles, setAttachedFiles] = useState(milestone?.files || []);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const [notifyingFiles, setNotifyingFiles] = useState(false);
  const [error, setError] = useState('');

  const addFiles = (fileList) => {
    const mapped = Array.from(fileList).map((file) => (
      file.size > MAX_UPLOAD_BYTES
        ? { file, status: 'error', progress: 0, error: 'Too large (max ' + MAX_UPLOAD_MB + ' MB)' }
        : { file, status: 'pending', progress: 0, error: '' }
    ));
    setEntries((prev) => [...prev, ...mapped]);
  };

  const uploadOne = async (index, file, milestoneId) => {
    setEntries((es) => es.map((en, i) => (i === index ? { ...en, status: 'uploading', progress: 0, error: '' } : en)));
    try {
      await uploadMilestoneFile(jwtToken, project._id, milestoneId, file, {
        onProgress: (pct) => setEntries((es) => es.map((en, i) => (i === index ? { ...en, progress: pct, status: pct >= 100 ? 'saving' : 'uploading' } : en))),
      });
      setEntries((es) => es.map((en, i) => (i === index ? { ...en, status: 'done', progress: 100 } : en)));
      return true;
    } catch (err) {
      setEntries((es) => es.map((en, i) => (i === index ? { ...en, status: 'error', error: err.message } : en)));
      return false;
    }
  };

  const submit = async (e, notify = false) => {
    e?.preventDefault(); setError(''); setLoading(true);
    try {
      let createdOrUpdated;
      if (isEdit) {
        createdOrUpdated = await apiUpdateMilestone(jwtToken, project._id, milestone._id, form);
        if (notify) {
          try {
            await apiNotifyMilestone(jwtToken, project._id, milestone._id, {
              toEmail: client.email, clientName: client.name, projectName: project.name,
              milestoneTitle: form.title, message: form.message,
            });
          } catch (notifyErr) { setError('Saved, but notification email failed: ' + notifyErr.message); }
        }
      } else {
        createdOrUpdated = await apiAddMilestone(jwtToken, project._id, form);
      }
      const files = entries.map((en) => en.file);
      let allOk = true;
      for (let i = 0; i < entries.length; i++) {
        if (entries[i].status === 'done') continue;
        const ok = await uploadOne(i, files[i], createdOrUpdated._id);
        if (!ok) allOk = false;
      }
      if (allOk) {
        onSuccess();
      } else {
        if (onRefreshClient) await onRefreshClient();
        setError('Some files could not be uploaded. Retry the failed ones below.');
      }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleDeleteFile = async () => {
    if (!fileToDelete) return;
    const fileId = fileToDelete.id || fileToDelete._id?.toString();
    setDeletingFile(fileId);
    setError('');
    try {
      await apiDeleteMilestoneFile(jwtToken, project._id, milestone._id, fileId);
      setAttachedFiles((files) => files.filter((file) => (file.id || file._id?.toString()) !== fileId));
      setFileToDelete(null);
      if (onRefreshClient) Promise.resolve().then(onRefreshClient).catch(() => {});
    } catch (err) {
      setFileToDelete(null);
      setError('Failed to remove file: ' + err.message);
    }
    finally { setDeletingFile(null); }
  };

  const handleNotifyFiles = async () => {
    if (!zohoConnected) { setError('Zoho is not connected — cannot send email.'); return; }
    setNotifyingFiles(true);
    try {
      await apiNotifyMilestoneFiles(jwtToken, project._id, milestone._id, {
        toEmail: client.email, clientName: client.name, projectName: project.name, milestoneTitle: milestone.title,
      });
      if (onRefreshClient) await onRefreshClient();
      alert('Client notified about the files!');
    } catch (err) {
      if (onRefreshClient) await onRefreshClient();
      setError('Failed to send notification: ' + err.message);
    } finally { setNotifyingFiles(false); }
  };

  return (
    <ModalPortal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal milestone-editor-modal" onClick={(e) => e.stopPropagation()}>
          <div className="milestone-editor-header">
            <div>
              <span className="milestone-editor-eyebrow">{project.name}</span>
              <h2>{isEdit ? 'Edit milestone' : 'Create milestone'}</h2>
              <p>{isEdit ? 'Update the work, client message and deliverables.' : 'Define the next piece of work for this project.'}</p>
            </div>
            <button type="button" onClick={onClose} aria-label="Close milestone editor"><X size={18} /></button>
          </div>
          <form onSubmit={(e) => submit(e, false)} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, overflow: 'hidden' }}>
            <div className="scroll-hidden milestone-editor-body">
              <section className="milestone-editor-section">
                <div className="milestone-editor-section-heading">
                  <span>01</span><div><strong>Milestone details</strong><small>The internal title, description and current stage.</small></div>
                </div>
                <div className="milestone-editor-fields">
                  <div className="milestone-editor-field"><label className="field-label">Title</label>
                    <input className="input" placeholder="e.g. Final cover design" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required /></div>
                  <div className="milestone-editor-field"><label className="field-label">Description</label>
                    <textarea className="input" placeholder="What needs to be completed?" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} /></div>
                </div>
                <fieldset className="milestone-status-fieldset">
                  <legend className="field-label">Status</legend>
                  <div className="milestone-status-options">
                    {[
                      { value: 'in_process', label: 'In progress', hint: 'Work is underway', tone: 'warning' },
                      { value: 'completed', label: 'Completed', hint: 'Ready and finished', tone: 'success' },
                      { value: 'approval_needed', label: 'Awaiting client approval', hint: 'Client action required', tone: 'warning' },
                    ].map((status) => (
                      <button
                        key={status.value}
                        type="button"
                        className={`milestone-status-option ${status.tone}${form.status === status.value ? ' active' : ''}`}
                        onClick={() => setForm((current) => ({ ...current, status: status.value }))}
                        aria-pressed={form.status === status.value}
                      >
                        <span className="milestone-status-dot" />
                        <span><strong>{status.label}</strong><small>{status.hint}</small></span>
                      </button>
                    ))}
                  </div>
                </fieldset>
              </section>

              {isEdit && (
                <section className="milestone-editor-section">
                  <div className="milestone-editor-section-heading">
                    <span>02</span><div><strong>Client delivery</strong><small>Share context, manage files and review approvals.</small></div>
                  </div>
                  <div className="milestone-editor-field milestone-client-message">
                    <label className="field-label">Message for client</label>
                    <textarea className="input" placeholder="Add a short note the client will see with this milestone..." value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} rows={2} />
                  </div>

                  {attachedFiles.length > 0 && (
                    <div className="milestone-editor-files">
                      <div className="milestone-editor-subheading"><span>Attached files</span><small>{attachedFiles.length}</small></div>
                      <div className="scroll-hidden milestone-editor-file-list">
                        {attachedFiles.map((f) => (
                          <div key={f.id || f._id?.toString()} className="milestone-editor-file-row">
                            <FileLink file={f} style={{ fontSize: 13, color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} />
                            {canDeleteTeamContent(user, f, 'uploadedByRole') && <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)', marginLeft: 8, padding: '2px 6px', flexShrink: 0 }} disabled={deletingFile === (f.id || f._id?.toString())} onClick={() => setFileToDelete(f)} title="Remove file"><Trash2 size={13} /></button>}
                          </div>
                        ))}
                      </div>
                      <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8, border: '1px solid var(--border)', display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled={notifyingFiles || !zohoConnected} onClick={handleNotifyFiles} title={!zohoConnected ? 'Zoho is not connected' : 'Email client about these files'}>
                        <Bell size={13} /> {notifyingFiles ? 'Sending...' : 'Notify Client About Files'}
                      </button>
                    </div>
                  )}

                  {milestone?.approvals?.length > 0 && (
                    <div className="milestone-editor-approvals">
                      <div className="milestone-editor-subheading"><span><CheckCircle size={13} /> Client approvals</span><small>{milestone.approvals.length}</small></div>
                      <div className="milestone-editor-approval-list">
                        {milestone.approvals.map((ap, i) => (
                          <div key={i} className="milestone-editor-approval">
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--success)', marginBottom: 4 }}>Approval {i + 1} — {new Date(ap.approvedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                            {ap.filesSnapshot.length === 0 ? <div style={{ fontSize: 12, color: 'var(--success)' }}>No files attached at approval</div> : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {ap.filesSnapshot.map((f, fi) => (
                                  <FileLink key={fi} file={f} iconSize={11} style={{ fontSize: 12, color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} />
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="milestone-upload-section">
                    <div className="milestone-upload-heading"><Paperclip size={14} /><span>Upload new files</span><small>Up to {MAX_UPLOAD_MB} MB each</small></div>
                    <div className="milestone-upload-queue">
                      {entries.map((en, i) => (
                        <div key={i} className={`milestone-upload-entry ${en.status}`}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <span style={{ fontSize: 13, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{en.file.name}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                              {en.status === 'uploading' && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{en.progress}%</span>}
                              {en.status === 'saving' && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Saving…</span>}
                              {en.status === 'done' && <CheckCircle size={14} color="var(--success)" />}
                              {en.status === 'error' && <button type="button" className="btn btn-ghost btn-sm" style={{ border: '1px solid var(--border)', padding: '2px 8px' }} onClick={() => uploadOne(i, en.file, milestone._id)}>Retry</button>}
                              {(en.status === 'pending' || en.status === 'error') && (
                                <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)', padding: '2px 6px' }} onClick={() => setEntries(entries.filter((_, idx) => idx !== i))}><X size={13} /></button>
                              )}
                            </div>
                          </div>
                          {(en.status === 'uploading' || en.status === 'saving') && (
                            <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, marginTop: 6, overflow: 'hidden' }}><div style={{ height: '100%', width: en.progress + '%', background: 'var(--accent)' }} /></div>
                          )}
                          {en.status === 'error' && en.error && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{en.error}</div>}
                        </div>
                      ))}
                    </div>
                    <label className="milestone-upload-dropzone">
                      <Upload size={20} />
                      <span><strong>Choose files</strong><small>PDFs, images and project documents</small></span>
                      <input type="file" multiple onChange={(e) => { if (e.target.files.length > 0) addFiles(e.target.files); e.target.value = null; }} />
                    </label>
                    {client.gdriveFolderId && (
                      <div className="milestone-drive-note">Legacy files also live in the <a href={`https://drive.google.com/drive/folders/${client.gdriveFolderId}`} target="_blank" rel="noreferrer">Google Drive folder</a>.</div>
                    )}
                  </div>
                </section>
              )}
              {error && <div className="milestone-editor-error">{error}</div>}
            </div>
            <div className="modal-footer milestone-editor-footer">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="spinner" /> : isEdit ? 'Save' : 'Add Milestone'}</button>
              {isEdit && form.status === 'approval_needed' && (
                <button type="button" className="btn btn-primary" style={{ background: 'var(--danger)' }} disabled={loading} onClick={(e) => submit(e, true)}>{loading ? <span className="spinner" /> : 'Notify & Save'}</button>
              )}
            </div>
          </form>
        </div>
      </div>
      {fileToDelete && (
        <ConfirmDialog
          title="Remove attachment?"
          message={`“${fileToDelete.name}” will be permanently removed from this milestone.`}
          confirmLabel="Remove file"
          loadingLabel="Removing..."
          isDangerous
          isLoading={deletingFile === (fileToDelete.id || fileToDelete._id?.toString())}
          onConfirm={handleDeleteFile}
          onCancel={() => setFileToDelete(null)}
        />
      )}
    </ModalPortal>
  );
}
